import { useState, useRef, useEffect } from "react";
import { Bookmark as BookmarkIcon, BookmarkPlus, X, Tag } from "lucide-react";
import clsx from "clsx";
import { useReplayStore, type Bookmark } from "@/store/replay";
import { formatDate } from "@/lib/format";
import type { Candle } from "@/hooks/useCandles";

interface BookmarksProps {
  /** Currently loaded candles — used to translate bookmark.candleTs → index when jumping */
  candles: Candle[];
  /** Current chart context — needed when adding a new bookmark */
  symbol: string;
  exchange: string;
  timeframe: string;
}

export function Bookmarks({ candles, symbol, exchange, timeframe }: BookmarksProps) {
  const [open, setOpen] = useState(false);
  const [labelInput, setLabelInput] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const { active, currentIndex, bookmarks, addBookmark, removeBookmark, jumpToBookmark } =
    useReplayStore();

  // Close on click outside
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setLabelInput("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const canAdd = active && currentIndex !== null && candles[currentIndex];

  const handleAdd = () => {
    if (!canAdd) return;
    const c = candles[currentIndex!];
    addBookmark({
      symbol,
      exchange,
      timeframe,
      candleTs: c.ts,
      label: labelInput.trim() || undefined,
    });
    setLabelInput("");
  };

  // Filter bookmarks by current symbol+timeframe for the top "current chart" section
  const currentChart = bookmarks.filter(
    (b) => b.symbol === symbol && b.exchange === exchange && b.timeframe === timeframe
  );
  const otherCharts = bookmarks.filter(
    (b) => !(b.symbol === symbol && b.exchange === exchange && b.timeframe === timeframe)
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "flex items-center gap-1.5 rounded px-2.5 py-1 text-xs transition-colors",
          "text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
        )}
        aria-label="Bookmarks"
      >
        <BookmarkIcon className="h-3.5 w-3.5" />
        <span>Bookmarks</span>
        {bookmarks.length > 0 && (
          <span className="rounded bg-bg-elevated px-1.5 py-px text-2xs text-text-primary">
            {bookmarks.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute bottom-full right-0 z-30 mb-1.5 w-80 rounded-md border border-border bg-bg-elevated shadow-elevated animate-slide-up">
          {/* Add row — only when in replay mode at a specific candle */}
          {canAdd && (
            <div className="border-b border-border p-2.5">
              <div className="mb-1.5 text-2xs uppercase tracking-wider text-text-muted">
                Bookmark candle {currentIndex! + 1} · {formatDate(candles[currentIndex!].ts)}
              </div>
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={labelInput}
                  onChange={(e) => setLabelInput(e.target.value)}
                  placeholder="Optional label (e.g. 'Earnings beat')"
                  className="flex-1 rounded bg-bg-panel px-2 py-1 text-xs text-text-primary placeholder:text-text-muted focus:outline-none"
                  maxLength={60}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAdd();
                  }}
                />
                <button
                  onClick={handleAdd}
                  className="flex items-center gap-1 rounded bg-accent px-2 py-1 text-2xs text-white hover:bg-accent-hover"
                >
                  <BookmarkPlus className="h-3 w-3" />
                  Save
                </button>
              </div>
            </div>
          )}

          <div className="max-h-72 overflow-y-auto py-1">
            {bookmarks.length === 0 && (
              <div className="px-3 py-6 text-center text-2xs text-text-muted">
                No bookmarks yet. Enter replay mode (R) and press B at any candle.
              </div>
            )}

            {currentChart.length > 0 && (
              <Section title="Current chart">
                {currentChart.map((b) => (
                  <BookmarkRow
                    key={b.id}
                    bookmark={b}
                    onJump={() => {
                      jumpToBookmark(b.id, candles);
                      setOpen(false);
                    }}
                    onRemove={() => removeBookmark(b.id)}
                    isCurrentChart
                  />
                ))}
              </Section>
            )}

            {otherCharts.length > 0 && (
              <Section title="Other charts">
                {otherCharts.map((b) => (
                  <BookmarkRow
                    key={b.id}
                    bookmark={b}
                    onJump={() => {
                      jumpToBookmark(b.id, candles);
                      setOpen(false);
                    }}
                    onRemove={() => removeBookmark(b.id)}
                    isCurrentChart={false}
                  />
                ))}
              </Section>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="px-3 py-1 text-2xs font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </div>
      {children}
    </div>
  );
}

function BookmarkRow({
  bookmark,
  onJump,
  onRemove,
  isCurrentChart,
}: {
  bookmark: Bookmark;
  onJump: () => void;
  onRemove: () => void;
  isCurrentChart: boolean;
}) {
  return (
    <div className="group flex items-center gap-2 px-3 py-1.5 hover:bg-bg-hover">
      <button
        onClick={onJump}
        className="flex flex-1 items-center gap-2 text-left"
        disabled={!isCurrentChart}
        title={isCurrentChart ? "Jump to this candle" : "Switch chart to jump"}
      >
        <Tag className="h-3 w-3 shrink-0 text-text-muted" />
        <div className="flex-1 overflow-hidden">
          <div className="truncate text-xs text-text-primary">
            {bookmark.label || `${bookmark.symbol} · ${formatDate(bookmark.candleTs)}`}
          </div>
          <div className="text-2xs text-text-muted font-mono">
            {bookmark.symbol} · {bookmark.timeframe} · {formatDate(bookmark.candleTs)}
          </div>
        </div>
      </button>
      <button
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-bear transition-opacity"
        aria-label="Remove bookmark"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
